import { useMemo } from 'react';
import { Grid as UikGrid } from '@appd/uik';
import { GridProps } from './models';
import { useRowSource } from './hooks/useRowSource';
import { logger } from '../../../utils';
import { getColumnDefs } from './utils';
import { table } from 'console';

/**
 * Renders a grid component using uik Grid (AG Grid) with the following supports/transformations:
 * - auto value formatting
 * - custom value formatting
 * - server-side data pagination with dashify datasource model
 *
 * @param {GridProps<TData>} props - The props for the Grid component.
 * @param props... - Additional props to be spread onto the underlying UikGrid component.
 */
export function Grid<TData>({
  fetchNextPage,
  hasNextPage,
  isFetching,
  isFetchingNextPage,
  data,
  columnOverrides,
  tableOptions,
  metadata,
  ...props
}: GridProps<TData>): JSX.Element {
  const isClientSideData = !fetchNextPage;
  const columns = useMemo(() => {
    const newColumns =
      data && data.length > 0 ? getColumnDefs(data, columnOverrides, tableOptions, isClientSideData, metadata) : [];
    logger.log('Grid columns updated:', { newColumns });
    return newColumns;
  }, [!!data, columnOverrides, tableOptions, isClientSideData, metadata]);

  const { rows, serverSideInitialRowCount } = useRowSource({
    data,
    fetchNextPage,
    isFetching,
    isFetchingNextPage,
    hasNextPage,
  });

  return (
    <UikGrid
      columns={columns}
      {...props}
      rows={rows}
      serverSideInitialRowCount={!!fetchNextPage ? serverSideInitialRowCount : undefined}
      maxConcurrentDatasourceRequests={!!fetchNextPage ? 1 : undefined}
    />
  );
}
